import TaskCellStatus from "@/components/SingleLineTask/SingleLineTaskCells/taskCellStatus.component";
import { ROUTE_SEARCH } from "@/constants/routes.constants";
import { useUser } from "@/hooks/AppContext/user.hook";
import { TableCell, TableRow } from "@/shadcn/components/ui/table";
import { SearchTaskAdvanced, SearchTaskBasic } from "@/types";
import { Row } from "@tanstack/react-table";
import {
  format,
  isPast,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
} from "date-fns";
import { User } from "lucide-react";
import React, { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
type SearchTaskUnion = SearchTaskBasic | SearchTaskAdvanced;

type ViewSearchResultsRowMobileProps = {
  row: Row<SearchTaskUnion>;
  visibleColumns: string[];
  inlineEditingDisabled?: boolean;
};

const ViewSearchResultsRowMobile: React.FC<ViewSearchResultsRowMobileProps> = ({
  row,
  visibleColumns,
  inlineEditingDisabled = false,
}) => {
  const nav = useNavigate();
  const location = useLocation();
  const { getUserProfileImageByID } = useUser();

  function openTaskDetails(taskID: number) {
    const searchParams = new URLSearchParams(location.search);
    const newPath = `${ROUTE_SEARCH}/${taskID}?${searchParams.toString()}`;
    nav(newPath);
  }

  function handleSingleClick() {
    if (inlineEditingDisabled) {
      return;
    }
    openTaskDetails(row.original.id);
  }

  function completeTask(taskID: number) {}

  function unCompleteTask(taskID: number) {}

  function formatCellDate(date: string | null): ReactNode {
    if (!date) return <div className="">No Due Date</div>;

    const parsedDate = parseISO(date);
    let dateShown;
    let colorClass = "";
    if (isToday(parsedDate)) {
      colorClass = "text-green-400";
      dateShown = "Today";
    } else if (isTomorrow(parsedDate)) {
      colorClass = "text-indigo-400";
      dateShown = "Tomorrow";
    } else if (isPast(parsedDate)) {
      colorClass = "text-red-400";
      if (isYesterday(parsedDate)) {
        dateShown = "Yesterday";
      }
    }

    if (isThisYear(parsedDate)) {
      dateShown = dateShown ?? format(parsedDate, "yyy/MM/dd");
    } else {
      dateShown = dateShown ?? format(parsedDate, "yyy/MM/dd");
    }

    return <div className={`${colorClass} select-none`}>{dateShown}</div>;
  }

  return (
    <TableRow
      data-state={row.getIsSelected() && "selected"}
      onClick={handleSingleClick}
      className=""
    >
      {visibleColumns.includes("status") && (
        <TableCell>
          <TaskCellStatus
            className="capitalize select-none"
            completed={row.getValue("status") === "complete"}
            taskID={row.original.id}
            completeTask={completeTask}
            unCompleteTask={unCompleteTask}
          />
        </TableCell>
      )}
      {visibleColumns.includes("title") && (
        <TableCell>
          {row.getValue("title") ? (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-300 truncate max-w-[35vw] select-none">
              {row.getValue("title")}
            </div>
          ) : (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-300 select-none">
              No Title
            </div>
          )}
        </TableCell>
      )}
      {visibleColumns.includes("due_date") && (
        <TableCell>
          {row.getValue("due_date") ? (
            formatCellDate(row.getValue("due_date"))
          ) : (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-300 select-none">
              No Due Date
            </div>
          )}
        </TableCell>
      )}
      {visibleColumns.includes("assignee_name") && (
        <TableCell>
          {row.original.assignee_id ? (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-300 truncate max-w-[35vw] select-none flex items-center justify-center">
              {getUserProfileImageByID(row.original.assignee_id) ? (
                <img
                  className="w-6 h-6 rounded-full"
                  src={getUserProfileImageByID(row.original.assignee_id)}
                  alt="User Profile"
                />
              ) : (
                <div className="bg-primary w-6 h-6 rounded-full flex items-center justify-center">
                  <User />
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-300 select-none">
              None
            </div>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

export default ViewSearchResultsRowMobile;
